/**
 * BrandButton — Reusable CTA Component
 *
 * Implements Audi brand button spec:
 * - Primary: bg-brand-btn, white text, pill shape (radius-brand-btn = 50px)
 * - Secondary: border-1 white, transparent bg, white text
 * - Text: no background, no border — looks like a link with tracking
 *
 * Loading state replaces button content with a progress bar.
 * Progress bar uses radius-brand-progress (0px — square corners for Audi).
 *
 * Accessibility:
 * - aria-disabled=true when disabled or loading (keeps element focusable,
 *   prevents click, communicates state to screen readers)
 * - aria-busy=true while loading (signals live progress to assistive tech)
 * - onClick is suppressed when disabled/loading (no double-submit risk)
 *
 * @see docs/ims-brand-frontends/03-design-system.md#buttons
 */
import { memo } from 'react';
import { cn } from '../../utils/cn';

type ButtonVariant = 'primary' | 'secondary' | 'text';

interface BrandButtonProps {
  variant?: ButtonVariant;
  loading?: boolean;
  loadingProgress?: number; // 0–100
  disabled?: boolean;
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-btn border border-brand-btn-border text-brand-text hover:bg-brand-btn-hover hover:border-brand-btn-border-hover active:bg-brand-btn-hover active:border-brand-btn-border-hover active:scale-95',
  secondary:
    'bg-transparent text-brand-text border border-brand-text/40 hover:bg-brand-text/10 hover:border-brand-text/60',
  text: 'bg-transparent text-brand-text-secondary border-transparent hover:text-brand-text underline-offset-4 hover:underline',
};

export const BrandButton = memo(function BrandButton({
  variant = 'primary',
  loading = false,
  loadingProgress = 0,
  disabled = false,
  className,
  onClick,
  children,
}: BrandButtonProps) {
  const base =
    'relative w-full h-12 flex items-center justify-center rounded-brand-btn text-sm font-normal tracking-[0.06em] transition-all duration-200 select-none';

  const isInert = disabled || loading;

  return (
    <button
      onClick={isInert ? undefined : onClick}
      disabled={isInert}
      aria-disabled={isInert}
      aria-busy={loading}
      className={cn(
        base,
        variantClasses[variant],
        isInert && 'opacity-40 cursor-not-allowed',
        className,
      )}
    >
      {loading ? (
        /* Loading progress bar — square corners (Audi brand: radius-brand-progress = 0) */
        <span className="absolute inset-x-0 bottom-0 top-0 overflow-hidden rounded-brand-btn">
          <span
            className="absolute inset-y-0 left-0 bg-white/30 transition-all duration-200"
            style={{ width: `${loadingProgress}%` }}
          />
          <span className="relative z-10 text-brand-text text-xs tracking-[0.15em]">
            {loadingProgress}%
          </span>
        </span>
      ) : (
        children
      )}
    </button>
  );
});
